import { z } from 'zod';
import type {
  LogisticsCalculationCargoContent,
  LogisticsCalculationCargoLine,
  LogisticsCalculationCommonServiceLine,
  LogisticsCalculationContent,
  LogisticsCalculationCost,
  LogisticsCalculationCurrency,
  LogisticsCalculationSection,
} from './content.js';
import { LOGISTICS_CALCULATION_SECTIONS, LogisticsCalculationCurrencySchema } from './content.js';

export const LOGISTICS_CALCULATION_MONEY_REGEX = /^-?\d+\.\d{2}$/;
export const LogisticsCalculationMoneySchema = z.string().regex(LOGISTICS_CALCULATION_MONEY_REGEX);
/** The payable roubles the document prints without kopecks. */
export const LOGISTICS_CALCULATION_WHOLE_MONEY_REGEX = /^-?\d+$/;
export const LogisticsCalculationWholeMoneySchema = z
  .string()
  .regex(LOGISTICS_CALCULATION_WHOLE_MONEY_REGEX);

export const LogisticsCalculationPayableSchema = z
  .object({
    convertedUsd: LogisticsCalculationMoneySchema,
    payableUsd: LogisticsCalculationMoneySchema,
    convertedRub: LogisticsCalculationWholeMoneySchema,
    payableRub: LogisticsCalculationWholeMoneySchema,
    // The split settlement: dollars and yuan in dollars, roubles as roubles.
    splitUsd: LogisticsCalculationMoneySchema,
    payableSplitUsd: LogisticsCalculationMoneySchema,
    splitRub: LogisticsCalculationWholeMoneySchema,
    payableSplitRub: LogisticsCalculationWholeMoneySchema,
  })
  .strict();
export type LogisticsCalculationPayable = z.infer<typeof LogisticsCalculationPayableSchema>;

export const LogisticsCalculationMoneyTotalsSchema = z
  .object({
    usd: LogisticsCalculationMoneySchema,
    cny: LogisticsCalculationMoneySchema,
    rub: LogisticsCalculationMoneySchema,
  })
  .strict();
export type LogisticsCalculationMoneyTotals = z.infer<typeof LogisticsCalculationMoneyTotalsSchema>;

export const LogisticsCalculationLineResultSchema = z
  .object({
    amount: LogisticsCalculationMoneySchema,
    currency: LogisticsCalculationCurrencySchema.nullable(),
    convertedAmountUsd: LogisticsCalculationMoneySchema.nullable(),
    conversionCaption: z.string().nullable(),
  })
  .strict();

const SectionResultSchema = z
  .object({ present: z.boolean(), totals: LogisticsCalculationMoneyTotalsSchema })
  .strict();

export const LogisticsCalculationCargoResultSchema = z
  .object({
    sections: z
      .object({
        chinaLogistics: SectionResultSchema,
        chinaExpenses: SectionResultSchema,
        rfExpenses: SectionResultSchema,
        rfLogistics: SectionResultSchema,
      })
      .strict(),
    lines: z.array(LogisticsCalculationLineResultSchema),
    totals: LogisticsCalculationMoneyTotalsSchema,
  })
  .strict();

export const LogisticsCalculationOutputSchema = z
  .object({
    cargos: z.array(LogisticsCalculationCargoResultSchema),
    commonServices: z
      .object({
        lines: z.array(LogisticsCalculationLineResultSchema),
        totals: LogisticsCalculationMoneyTotalsSchema,
      })
      .strict(),
    totals: LogisticsCalculationMoneyTotalsSchema,
    allRubReference: LogisticsCalculationMoneySchema.nullable(),
  })
  .strict();
export type LogisticsCalculationOutput = z.infer<typeof LogisticsCalculationOutputSchema>;

type Rational = { numerator: bigint; denominator: bigint };
const ZERO: Rational = { numerator: 0n, denominator: 1n };

function gcd(a: bigint, b: bigint): bigint {
  let left = a < 0n ? -a : a;
  let right = b < 0n ? -b : b;
  while (right !== 0n) {
    const remainder = left % right;
    left = right;
    right = remainder;
  }
  return left;
}

function rational(numerator: bigint, denominator: bigint): Rational {
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function decimal(value: string): Rational {
  const normalized = value === '' ? '0' : value;
  const [whole = '0', fraction = ''] = normalized.split('.');
  return rational(BigInt(`${whole}${fraction}`), 10n ** BigInt(fraction.length));
}

function add(a: Rational, b: Rational): Rational {
  return rational(
    a.numerator * b.denominator + b.numerator * a.denominator,
    a.denominator * b.denominator,
  );
}

function multiply(a: Rational, b: Rational): Rational {
  return rational(a.numerator * b.numerator, a.denominator * b.denominator);
}

function divide(a: Rational, b: Rational): Rational {
  return rational(a.numerator * b.denominator, a.denominator * b.numerator);
}

/** Half away from zero at the given scale; the only rounding in this file. */
function scaledRound(value: Rational, scale: bigint): bigint {
  const scaled = value.numerator * scale;
  const quotient = scaled / value.denominator;
  const remainder = scaled % value.denominator;
  return (remainder < 0n ? -remainder : remainder) * 2n >= value.denominator
    ? quotient + (value.numerator < 0n ? -1n : 1n)
    : quotient;
}

function money(value: Rational): string {
  const coefficient = scaledRound(value, 100n);
  const negative = coefficient < 0n;
  const digits = (negative ? -coefficient : coefficient).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

function wholeUnit(value: Rational): string {
  return scaledRound(value, 1n).toString();
}

function cnyPerUsdRate(rates: {
  cnyPerUsdRate?: string | undefined;
  rubPerUsdRate: string;
  rubPerCnyRate?: string | undefined;
}): Rational {
  return rates.rubPerCnyRate === undefined
    ? decimal(rates.cnyPerUsdRate!)
    : divide(decimal(rates.rubPerUsdRate), decimal(rates.rubPerCnyRate));
}

/**
 * Decimal-safe sum of already-frozen money strings.
 *
 * WHY: the customer sheet needs per-group face sums that
 * `LogisticsCalculationOutputSchema` deliberately does not carry, and that
 * schema re-parses every stored snapshot, so it must not gain a key. Exposing
 * the summation instead keeps the projection off floating-point arithmetic.
 */
export function sumLogisticsCalculationMoney(values: readonly string[]): string {
  return money(values.reduce((total, value) => add(total, decimal(value)), ZERO));
}

function emptyTotals(): Record<LogisticsCalculationCurrency, Rational> {
  return { USD: ZERO, CNY: ZERO, RUB: ZERO };
}

function wireTotals(
  totals: Record<LogisticsCalculationCurrency, Rational>,
): LogisticsCalculationMoneyTotals {
  return { usd: money(totals.USD), cny: money(totals.CNY), rub: money(totals.RUB) };
}

function addCost(
  totals: Record<LogisticsCalculationCurrency, Rational>,
  cost: LogisticsCalculationCost | undefined,
): void {
  if (cost === undefined) return;
  totals[cost.currency] = add(totals[cost.currency], decimal(cost.amount));
}

/** The typed cost cells of a cargo, each counted once into its own currency. */
function addCargoCosts(
  totals: Record<LogisticsCalculationCurrency, Rational>,
  cargo: LogisticsCalculationCargoContent,
): void {
  addCost(totals, cargo.transport);
  addCost(totals, cargo.insurance);
  addCost(totals, cargo.packaging);
  for (const extra of cargo.extras ?? []) addCost(totals, extra);
  addCost(totals, cargo.prr);
  addCost(totals, cargo.clientDelivery);
}

function addLine(
  totals: Record<LogisticsCalculationCurrency, Rational>,
  line: LogisticsCalculationCargoLine | LogisticsCalculationCommonServiceLine,
  cnyPerUsdRate: Rational,
): z.infer<typeof LogisticsCalculationLineResultSchema> {
  const face = multiply(decimal(line.quantity), decimal(line.rate));
  if (line.currency === null || line.currency === undefined) {
    return {
      amount: money(face),
      currency: null,
      convertedAmountUsd: null,
      conversionCaption: null,
    };
  }
  if (line.convertToUsd) {
    const converted = divide(face, cnyPerUsdRate);
    totals.USD = add(totals.USD, converted);
    return {
      amount: money(face),
      currency: line.currency,
      convertedAmountUsd: money(converted),
      conversionCaption: `${money(face)} ¥ = ${money(converted)} $`,
    };
  }
  totals[line.currency] = add(totals[line.currency], face);
  return {
    amount: money(face),
    currency: line.currency,
    convertedAmountUsd: null,
    conversionCaption: null,
  };
}

/** Decimal-safe canonical calculation. Converted CNY belongs only to USD totals. */
export function calculateLogisticsCalculation(
  content: LogisticsCalculationContent,
): LogisticsCalculationOutput {
  const cnyRate = cnyPerUsdRate(content);
  const documentTotals = emptyTotals();
  const cargos = content.cargos.map((cargo) => {
    const cargoTotals = emptyTotals();
    const sectionTotals = new Map<
      LogisticsCalculationSection,
      Record<LogisticsCalculationCurrency, Rational>
    >(LOGISTICS_CALCULATION_SECTIONS.map((section) => [section, emptyTotals()]));
    const lines = cargo.lines.map((line) => {
      const result = addLine(cargoTotals, line, cnyRate);
      addLine(sectionTotals.get(line.section)!, line, cnyRate);
      return result;
    });
    // The cost cells belong to the cargo, not to a section: the sheet prints
    // them as columns, and a stored `lines` document keeps its sections intact.
    addCargoCosts(cargoTotals, cargo);
    for (const currency of ['USD', 'CNY', 'RUB'] as const) {
      documentTotals[currency] = add(documentTotals[currency], cargoTotals[currency]);
    }
    const sections = Object.fromEntries(
      LOGISTICS_CALCULATION_SECTIONS.map((section) => [
        section,
        {
          present: cargo.lines.some((line) => line.section === section),
          totals: wireTotals(sectionTotals.get(section)!),
        },
      ]),
    ) as z.infer<typeof LogisticsCalculationCargoResultSchema>['sections'];
    return { sections, lines, totals: wireTotals(cargoTotals) };
  });

  const commonTotals = emptyTotals();
  const commonLines = content.commonServices.map((line) => addLine(commonTotals, line, cnyRate));
  for (const currency of ['USD', 'CNY', 'RUB'] as const) {
    documentTotals[currency] = add(documentTotals[currency], commonTotals[currency]);
  }
  const allRub = add(
    documentTotals.RUB,
    add(
      multiply(documentTotals.USD, decimal(content.rubPerUsdRate)),
      multiply(documentTotals.CNY, divide(decimal(content.rubPerUsdRate), cnyRate)),
    ),
  );
  return {
    cargos,
    commonServices: { lines: commonLines, totals: wireTotals(commonTotals) },
    totals: wireTotals(documentTotals),
    allRubReference: content.showAllRubReference ? money(allRub) : null,
  };
}

/**
 * The document's payable figures: the whole calculation in dollars and in
 * roubles, and the split settlement — the dollar and yuan costs in dollars
 * beside the rouble costs as they stand — each divided by the manager's divisor
 * when the payment mode has one.
 *
 * INVARIANT: convert, add, divide, then round — once, at the end. Rounding a
 * converted total to cents before dividing it prints different money.
 *
 * WHY here and not in the projection: the exact `Rational` arithmetic and the
 * rounding are private to this file, and a `Number` round trip loses money at
 * magnitudes a 40-character decimal input already reaches. The roubles round to
 * a whole rouble because the formatter cannot tell a payable cell from an
 * ordinary one.
 */
export function calculateLogisticsCalculationPayable(
  totals: LogisticsCalculationMoneyTotals,
  rates: {
    cnyPerUsdRate?: string | undefined;
    rubPerUsdRate: string;
    rubPerCnyRate?: string | undefined;
  },
  paymentDivisor: string | undefined,
): LogisticsCalculationPayable {
  const cnyRate = cnyPerUsdRate(rates);
  const rubRate = decimal(rates.rubPerUsdRate);
  const splitUsd = add(decimal(totals.usd), divide(decimal(totals.cny), cnyRate));
  const splitRub = decimal(totals.rub);
  const usd = add(splitUsd, divide(splitRub, rubRate));
  const rub = add(
    decimal(totals.rub),
    add(
      multiply(decimal(totals.usd), rubRate),
      multiply(decimal(totals.cny), divide(rubRate, cnyRate)),
    ),
  );
  const divisor =
    paymentDivisor === undefined || paymentDivisor === '' ? null : decimal(paymentDivisor);
  const payable = (value: Rational): Rational =>
    divisor === null ? value : divide(value, divisor);
  return {
    convertedUsd: money(usd),
    payableUsd: money(payable(usd)),
    convertedRub: wholeUnit(rub),
    payableRub: wholeUnit(payable(rub)),
    splitUsd: money(splitUsd),
    payableSplitUsd: money(payable(splitUsd)),
    splitRub: wholeUnit(splitRub),
    payableSplitRub: wholeUnit(payable(splitRub)),
  };
}
